// Cierre de la venta: cupón emitido y lo que se lleva el cliente. Puro, sin React.
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import type { ReservedLine } from '@/domains/sales/wizard/types';
import {
    promotionBonusDisplayStatus,
    promotionBonusText,
    type PromotionBonusDisplayStatus,
} from '@/domains/sales/wizard/promotionBonusDisplay';
import { formatMXN } from '@/shared/lib/format/money';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import {
    COUPON_MAX_POLLS,
    COUPON_POLL_MS,
    WALLET_POLL_FAST_MS,
    WALLET_POLL_FAST_READS,
    WALLET_POLL_MAX_READS,
    WALLET_POLL_SLOW_MS,
} from '../constants';
import type {
    DiscountValue,
    GetSaleCouponResponse,
    SaleCouponAvailability,
    SaleEntitlement,
} from '../api/wizardPromotionsContract';

/** La espera termina al llegar al tope de lecturas o cuando la última lectura falló. */
export function couponPollingDone(reads: number, failed: boolean): boolean {
    return failed || reads >= COUPON_MAX_POLLS;
}

/** Textos de la tarjeta de beneficios; en una venta cerrada quien lee no es el dueño del cupón. */
export interface BenefitsCopy {
    title: string;
    couponTitle: string;
    generating: string;
    stillGenerating: string;
}

export type BenefitsView = 'CLOSING' | 'CLOSED_SALE';

const BENEFITS_COPY: Record<BenefitsView, BenefitsCopy> = {
    CLOSING: {
        title: 'Lo que se lleva',
        couponTitle: 'Cupón para tu próxima compra',
        generating: 'Se está generando tu cupón…',
        stillGenerating: 'Tu cupón sigue generándose.',
    },
    CLOSED_SALE: {
        title: 'Se lleva',
        couponTitle: 'Cupón del cliente',
        generating: 'Se está generando el cupón del cliente…',
        stillGenerating: 'El cupón del cliente sigue generándose.',
    },
};

export function benefitsCopy(view: BenefitsView): BenefitsCopy {
    return BENEFITS_COPY[view];
}

/** Reintenta solo mientras el cupón se genera y la espera no terminó; `reads` cuenta éxitos y fallas. */
export function couponPollInterval(
    availability: SaleCouponAvailability | undefined,
    reads: number,
    failed = false,
): number | false {
    return availability === 'GENERATING' && !couponPollingDone(reads, failed)
        ? COUPON_POLL_MS
        : false;
}

/** Sondeo del Wallet mientras siga sin desenlace (PENDING, IN_FLIGHT o UNVERIFIED), con tope. */
export function walletPollInterval(
    entitlements: readonly SaleEntitlement[] | undefined,
    reads: number,
    failed = false,
): number | false {
    const open = (entitlements ?? []).some((e) => {
        if (e.kind !== 'WALLET') return false;
        const display = promotionBonusDisplayStatus({ kind: 'SALE', state: e.state });
        return display === 'IN_PROCESS' || display === 'IN_REVIEW';
    });
    if (!open || failed || reads >= WALLET_POLL_MAX_READS) return false;
    return reads < WALLET_POLL_FAST_READS ? WALLET_POLL_FAST_MS : WALLET_POLL_SLOW_MS;
}

/** La espera más corta entre el cupón y, solo en el cierre del wizard, el Wallet. */
export function saleCouponPollInterval(
    data: Pick<GetSaleCouponResponse, 'availability' | 'entitlements'> | undefined,
    reads: number,
    failed: boolean,
    closing: boolean,
): number | false {
    const intervals = [
        couponPollInterval(data?.availability, reads, failed),
        closing ? walletPollInterval(data?.entitlements, reads, failed) : false,
    ].filter((ms): ms is number => ms !== false);
    return intervals.length === 0 ? false : Math.min(...intervals);
}

/** Un bono de Wallet como lo ve el vendedor. */
export interface SaleWalletBonus {
    key: string;
    label: string;
    amountCents: number;
    displayStatus: PromotionBonusDisplayStatus;
}

/** Los Wallet de la venta como bonos; lo anulado o sin monto no se muestra. */
export function saleWalletBonusesOf(entitlements: readonly SaleEntitlement[]): SaleWalletBonus[] {
    return entitlements.flatMap((e, i) => {
        if (e.kind !== 'WALLET' || e.amountCents === null) return [];
        const displayStatus = promotionBonusDisplayStatus({ kind: 'SALE', state: e.state });
        if (displayStatus === null) return [];
        return [
            {
                key: `wallet-${String(i)}`,
                label: promotionBonusText(displayStatus, e.amountCents),
                amountCents: e.amountCents,
                displayStatus,
            },
        ];
    });
}

function money(cents: number | null): string {
    return cents === null ? '' : formatMXN(cents / 100);
}

/** Texto de una entrega; vacío si se anuló o es Wallet (ese sale como bono). */
export function entitlementText(entitlement: SaleEntitlement): string {
    if (entitlement.state === 'VOID') return '';
    const quantity = String(entitlement.quantity ?? 0);
    switch (entitlement.kind) {
        case 'WARRANTY':
            return `${quantity} meses más de garantía`;
        case 'AIRTIME':
            return `${quantity} recargas de tiempo aire de ${money(entitlement.amountCents)} · por entregar`;
        default:
            return '';
    }
}

/** Texto del descuento del cupón; vacío si no trae monto ni porcentaje. */
export function couponDiscountText(discount: DiscountValue): string {
    if (discount.type === 'PERCENTAGE')
        return discount.percentBps === null
            ? ''
            : `${String(discount.percentBps / 100)}% de descuento`;
    return discount.amountCents === null ? '' : `${money(discount.amountCents)} de descuento`;
}

/** Regalo con el nombre de su línea; es el SKU cuando la línea no trae ficha con nombre. */
export interface ClosingGift {
    sku: string;
    name: string;
}

/** Regalos que salieron con la venta. */
export function closingGifts(lines: readonly ReservedLine[] | undefined): ClosingGift[] {
    return (lines ?? [])
        .filter((l) => l.role === 'GIFT' && l.status !== 'RELEASING')
        .map((l) => ({ sku: l.sku, name: itemDisplayName({ sku: l.sku, ...l.product }) }));
}

/** Regalos de una venta ya cerrada. */
export function saleGifts(lines: readonly SaleLine[]): ClosingGift[] {
    return lines
        .filter((l) => l.role === 'GIFT')
        .map((l) => ({ sku: l.sku, name: itemDisplayName({ sku: l.sku, ...l.product }) }));
}

/** La sesión del wizard de una venta cerrada: el silo usa el sessionId como saleId. */
export function sessionIdOfSale(saleId: string): string {
    return saleId;
}
